import { AUTH_COOKIE_KEYS, AUTH_EVENTS, AUTH_STORAGE_KEYS } from '@/lib/auth/constants';
import type { AuthSession } from '@/lib/auth/types';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function setCookie(name: string, value: string, expiresAt: number) {
  if (!isBrowser()) {
    return;
  }

  const expires = new Date(expiresAt).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${expires}; SameSite=Lax${secure}`;
}

function clearCookie(name: string) {
  if (!isBrowser()) {
    return;
  }

  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

function dispatchAuthSessionChangedEvent() {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new Event(AUTH_EVENTS.sessionChanged));
}

function getSessionValidUntil(session: AuthSession): number {
  if (
    typeof session.refreshToken === 'string' &&
    session.refreshToken.trim().length > 0 &&
    typeof session.refreshExpiresAt === 'number' &&
    Number.isFinite(session.refreshExpiresAt)
  ) {
    return session.refreshExpiresAt;
  }

  return session.expiresAt;
}

export const authStorage = {
  setSession(session: AuthSession) {
    if (!isBrowser()) {
      return;
    }

    const validUntil = getSessionValidUntil(session);

    window.localStorage.setItem(AUTH_STORAGE_KEYS.session, JSON.stringify(session));
    setCookie(AUTH_COOKIE_KEYS.sessionPresent, '1', validUntil);
    setCookie(AUTH_COOKIE_KEYS.expiresAt, String(validUntil), validUntil);
    dispatchAuthSessionChangedEvent();
  },
  getSession(): AuthSession | null {
    if (!isBrowser()) {
      return null;
    }

    const value = window.localStorage.getItem(AUTH_STORAGE_KEYS.session);
    if (!value) {
      return null;
    }

    try {
      const session = JSON.parse(value) as AuthSession;
      const validUntil = getSessionValidUntil(session);
      const isExpired =
        !session?.token ||
        !session?.business?.id ||
        !session?.expiresAt ||
        !Number.isFinite(validUntil) ||
        validUntil <= Date.now();

      if (isExpired) {
        this.clearSession();
        return null;
      }

      return session;
    } catch {
      this.clearSession();
      return null;
    }
  },
  clearSession() {
    if (!isBrowser()) {
      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEYS.session);
    clearCookie(AUTH_COOKIE_KEYS.sessionPresent);
    clearCookie(AUTH_COOKIE_KEYS.expiresAt);
    dispatchAuthSessionChangedEvent();
  },
  isAuthenticated(): boolean {
    return Boolean(this.getSession());
  },
};
